import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  Video,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { portalBasePath } from "../../lib/portalPaths";
import {
  STAGE_LABEL,
  compareClientsByProjectDateDesc,
  isProspectStage,
  normalizeEngagementStage,
  OUTREACH_ACTIVE_COLUMNS,
} from "../../lib/engagementStages";
import {
  OFFERING_PHASES,
  OFFERING_KEYS,
  OFFERING_LABEL_SHORT,
  OFFERING_COLORS,
  canonicalOffering,
  offeringColor,
} from "../../config/offeringPhases";
import { isLocalApp } from "../../lib/env";
import AdminTodayWorkboard from "../../components/homeTasks/AdminHomeTasks";
import { loadAdminOpenToday } from "../../lib/homeTaskPreview";

const STAGE_BAR = {
  outreach: "#9AA3B2",
  met: "#001EFF",
  scope: "#001EFF",
};

/** Temporary schedule until admin Google calendar feed is wired to home. */
function buildMockSchedule() {
  const now = new Date();
  const at = (dayOffset, hour, minute = 0) => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hour, minute, 0, 0);
    return d;
  };
  const fmtTime = (d) =>
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const fmtDay = (d) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - start.getTime()) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };
  const isToday = (d) => {
    const a = new Date();
    return (
      d.getFullYear() === a.getFullYear() &&
      d.getMonth() === a.getMonth() &&
      d.getDate() === a.getDate()
    );
  };

  const meetings = [
    {
      id: "m1",
      title: "Strategy review",
      with: "Aurora Studio",
      start: at(0, 10, 0),
      end: at(0, 10, 45),
      joinUrl: "https://meet.google.com/abc-defg-hij",
      joinKind: "meet",
    },
    {
      id: "m2",
      title: "Internal stand-up",
      with: "TAĪSTU",
      start: at(0, 14, 30),
      end: at(0, 15, 0),
      joinUrl: "https://zoom.us/j/1234567890",
      joinKind: "zoom",
    },
    {
      id: "m3",
      title: "Proposal walkthrough",
      with: "Field Theory Labs",
      start: at(1, 14, 0),
      end: at(1, 15, 0),
      joinUrl: "https://meet.google.com/xyz-uvwx-yz",
      joinKind: "meet",
    },
    {
      id: "m4",
      title: "Discovery follow-up",
      with: "Harbor & Pine",
      start: at(2, 11, 30),
      end: at(2, 12, 0),
      joinUrl: null,
      joinKind: null,
    },
    {
      id: "m5",
      title: "Invoice check-in",
      with: "Soft Arc Studio",
      start: at(4, 16, 0),
      end: at(4, 16, 30),
      joinUrl: "https://zoom.us/j/9876543210",
      joinKind: "zoom",
    },
  ].map((m) => ({
    ...m,
    dayLabel: fmtDay(m.start),
    timeLabel: `${fmtTime(m.start)} – ${fmtTime(m.end)}`,
    timeShort: fmtTime(m.start),
    isToday: isToday(m.start),
  }));

  const tasks = [
    { id: "t1", title: "Send Soft Arc invoice reminder", dueLabel: "Today" },
    { id: "t2", title: "Draft Harbor & Pine notes", dueLabel: "Tomorrow" },
    { id: "t3", title: "Prep Field Theory deck", dueLabel: "Fri" },
    { id: "t4", title: "Follow up Northline intro", dueLabel: "Mon" },
  ];

  const ahead = [...meetings].sort((a, b) => a.start.getTime() - b.start.getTime());
  const today = ahead.filter((m) => m.isToday);

  return { ahead, today, tasks, next: today[0] || null };
}

function clientStatus(c) {
  const s = c.status || (c.archived ? "archived" : "active");
  return s === "offboarded" ? "archived" : s;
}

function phaseLabel(client) {
  const key = client?.active_phase;
  if (!key) return "—";
  const offering = canonicalOffering(client?.service_tier);
  const phases = OFFERING_PHASES[offering] || OFFERING_PHASES.web_design;
  return (phases || []).find((p) => p.key === key)?.label || key;
}

function parseDue(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysUntil(due) {
  if (!due) return null;
  return Math.round((startOfDay(due).getTime() - startOfDay().getTime()) / 86400000);
}

function attentionScore(c) {
  let score = 0;
  score += (c.open_approvals || 0) * 10;
  score += (c.unread_messages || 0) * 6;
  const n = daysUntil(parseDue(c.next_milestone?.due_date));
  if (n !== null) {
    if (n < 0) score += 12;
    else if (n === 0) score += 9;
    else if (n <= 3) score += 5;
    else if (n <= 7) score += 2;
  }
  return score;
}

function timeGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function firstName(user) {
  const raw = (user?.name || "").trim();
  if (!raw) return null;
  return raw.split(/\s+/)[0];
}

function joinLabel(kind) {
  if (kind === "zoom") return "Zoom";
  if (kind === "meet") return "Meet";
  return "Join";
}

function Panel({ children, className = "", testId }) {
  return (
    <section className={`neo-panel p-4 sm:p-5 ${className}`} data-testid={testId}>
      {children}
    </section>
  );
}

function PanelHead({ eyebrow, title, right }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow ? <div className="label-eyebrow mb-0.5">{eyebrow}</div> : null}
        <div className="font-heading text-lg tracking-tight !font-light">{title}</div>
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function JoinButton({ meeting, size = "md" }) {
  if (!meeting?.joinUrl) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">
        No link
      </span>
    );
  }
  const pad = size === "sm" ? "px-2.5 py-1.5 text-[10px]" : "px-3 py-2 text-[11px]";
  return (
    <a
      href={meeting.joinUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`neo-cta ${pad}`}
      data-testid={`home-join-${meeting.id}`}
    >
      <Video size={size === "sm" ? 12 : 14} />
      {joinLabel(meeting.joinKind)}
      <ExternalLink size={size === "sm" ? 10 : 12} />
    </a>
  );
}

export default function AdminHomePage() {
  const { switchClient, user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homeTasks, setHomeTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const schedule = useMemo(
    () => (isLocalApp() ? buildMockSchedule() : { ahead: [], today: [], tasks: [], next: null }),
    [],
  );

  const loadHomeTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const { data } = await api.get("/admin/home-tasks");
      const rows = Array.isArray(data?.tasks) ? data.tasks : [];
      setHomeTasks(
        rows.map((t) => ({
          id: t.id,
          deliverableId: t.deliverable_id,
          milestoneId: t.milestone_id,
          clientId: t.client_id,
          clientName: t.client_name,
          scopeTitle: t.scope_title,
          title: t.title,
          due: t.due || null,
          done: Boolean(t.done),
        })),
      );
    } catch (err) {
      if (isLocalApp()) {
        setHomeTasks(loadAdminOpenToday());
      } else {
        setHomeTasks([]);
        toast.error(err.response?.data?.detail || "Could not load tasks");
      }
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/clients");
        if (!cancelled) setClients(data || []);
      } catch {
        if (!cancelled) toast.error("Could not load home");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadHomeTasks();
  }, [loadHomeTasks]);

  const engagements = useMemo(
    () =>
      clients.filter((c) => {
        if (clientStatus(c) !== "active") return false;
        if (normalizeEngagementStage(c.engagement_stage) !== "active") return false;
        // Project engagements past timeline_end are archived server-side; exclude as a safety net.
        if ((c.engagement_type || "project") === "project" && c.timeline_end) {
          const end = new Date(c.timeline_end);
          if (!Number.isNaN(end.getTime())) {
            const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (endDay < today) return false;
          }
        }
        return true;
      }),
    [clients],
  );

  const prospects = useMemo(
    () => clients.filter((c) => isProspectStage(c.engagement_stage) && clientStatus(c) !== "archived"),
    [clients],
  );

  const pipelineData = useMemo(
    () =>
      OUTREACH_ACTIVE_COLUMNS.map((col) => ({
        key: col.key,
        label: col.label,
        count: prospects.filter((c) => {
          const stage = normalizeEngagementStage(c.engagement_stage, { defaultStage: "outreach" });
          return col.stages.includes(stage);
        }).length,
      })),
    [prospects],
  );

  const typeMix = useMemo(() => {
    const buckets = Object.fromEntries(OFFERING_KEYS.map((k) => [k, 0]));
    engagements.forEach((c) => {
      const tier = canonicalOffering(c.service_tier);
      if (buckets[tier] !== undefined) buckets[tier] += 1;
    });
    return Object.entries(buckets).map(([key, value]) => ({
      key,
      name: OFFERING_LABEL_SHORT[key] || key,
      value,
      color: OFFERING_COLORS[key],
    }));
  }, [engagements]);

  const needsYou = useMemo(
    () =>
      engagements
        .filter((c) => attentionScore(c) > 0)
        .sort((a, b) => attentionScore(b) - attentionScore(a))
        .slice(0, 6),
    [engagements],
  );

  const roster = useMemo(
    () => engagements.slice().sort(compareClientsByProjectDateDesc),
    [engagements],
  );

  const enterClient = (c) => {
    switchClient(c);
    navigate(portalBasePath(c));
  };

  const onToggleHomeTask = useCallback(
    async (id) => {
      const task = homeTasks.find((t) => t.id === id);
      if (!task?.milestoneId || !task.deliverableId) return;
      setHomeTasks((prev) => prev.filter((t) => t.id !== id));
      try {
        const { data } = await api.get(`/portal/milestones`, {
          params: { client_id: task.clientId },
        });
        const rows = Array.isArray(data) ? data : data?.milestones || [];
        const ms = rows.find((m) => m.id === task.milestoneId);
        const current = Array.isArray(ms?.deliverables) ? ms.deliverables : [];
        const deliverables = current.map((d) => {
          const did = String(d.id || "");
          const isTarget = did === String(task.deliverableId);
          return {
            id: d.id,
            label: d.label,
            done: isTarget ? true : Boolean(d.done),
            ...(d.due ? { due: d.due } : {}),
            ...(d.task_id ? { task_id: d.task_id } : {}),
          };
        });
        await api.patch(`/portal/milestones/${task.milestoneId}`, { deliverables });
        await loadHomeTasks();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not update task");
        await loadHomeTasks();
      }
    },
    [homeTasks, loadHomeTasks],
  );

  const onUpdateHomeTask = useCallback(
    async (id, patch = {}) => {
      const task = homeTasks.find((t) => t.id === id);
      if (!task?.milestoneId || !task.deliverableId) return;
      try {
        const { data } = await api.get(`/portal/milestones`, {
          params: { client_id: task.clientId },
        });
        const rows = Array.isArray(data) ? data : data?.milestones || [];
        const ms = rows.find((m) => m.id === task.milestoneId);
        const current = Array.isArray(ms?.deliverables) ? ms.deliverables : [];
        const deliverables = current.map((d) => {
          const did = String(d.id || "");
          const isTarget = did === String(task.deliverableId);
          const label =
            isTarget && patch.title != null ? String(patch.title).trim() : d.label;
          const due =
            isTarget && patch.due != null
              ? String(patch.due).slice(0, 10)
              : d.due || null;
          return {
            id: d.id,
            label,
            done: Boolean(d.done),
            ...(due ? { due } : {}),
            ...(d.task_id ? { task_id: d.task_id } : {}),
          };
        });
        await api.patch(`/portal/milestones/${task.milestoneId}`, { deliverables });
        await loadHomeTasks();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not update task");
      }
    },
    [homeTasks, loadHomeTasks],
  );

  const onDeleteHomeTask = useCallback(
    async (id) => {
      const task = homeTasks.find((t) => t.id === id);
      if (!task?.milestoneId || !task.deliverableId) return;
      setHomeTasks((prev) => prev.filter((t) => t.id !== id));
      try {
        const { data } = await api.get(`/portal/milestones`, {
          params: { client_id: task.clientId },
        });
        const rows = Array.isArray(data) ? data : data?.milestones || [];
        const ms = rows.find((m) => m.id === task.milestoneId);
        const current = Array.isArray(ms?.deliverables) ? ms.deliverables : [];
        const deliverables = current
          .filter((d) => String(d.id || "") !== String(task.deliverableId))
          .map((d) => ({
            id: d.id,
            label: d.label,
            done: Boolean(d.done),
            ...(d.due ? { due: d.due } : {}),
            ...(d.task_id ? { task_id: d.task_id } : {}),
          }));
        await api.patch(`/portal/milestones/${task.milestoneId}`, { deliverables });
        await loadHomeTasks();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not delete task");
        await loadHomeTasks();
      }
    },
    [homeTasks, loadHomeTasks],
  );

  const onAddHomeTask = useCallback(
    async (payload) => {
      const clientId = payload?.clientId;
      const label = String(payload?.title || "").trim();
      if (!clientId || !label) {
        toast.error("Pick an engagement and enter a task");
        return;
      }
      try {
        const { data } = await api.get(`/portal/milestones`, {
          params: { client_id: clientId },
        });
        const rows = Array.isArray(data) ? data : data?.milestones || [];
        const target =
          rows.find((m) => m.status === "in_progress") ||
          rows.find((m) => m.status !== "complete") ||
          rows[0];
        if (!target?.id) {
          toast.error("No milestone found for that engagement");
          return;
        }
        const current = Array.isArray(target.deliverables) ? target.deliverables : [];
        const deliverables = [
          ...current.map((d) => ({
            id: d.id,
            label: d.label,
            done: Boolean(d.done),
            ...(d.due ? { due: d.due } : {}),
            ...(d.task_id ? { task_id: d.task_id } : {}),
          })),
          {
            id: crypto.randomUUID(),
            label,
            done: false,
            ...(payload?.due ? { due: String(payload.due).slice(0, 10) } : {}),
          },
        ];
        await api.patch(`/portal/milestones/${target.id}`, { deliverables });
        toast.success("Task added");
        await loadHomeTasks();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not add task");
      }
    },
    [loadHomeTasks],
  );

  const onOpenHomeClient = (row) => {
    const match =
      clients.find((c) => c.id === row.clientId) ||
      clients.find(
        (c) => String(c.name || "").toLowerCase() === String(row.clientName || "").toLowerCase(),
      );
    if (match) enterClient(match);
    else toast.message(`${row.clientName} isn’t in your roster yet.`);
  };

  const greetName = firstName(user);
  const openTasks = homeTasks.filter((t) => !t.done).length;
  const maxStage = Math.max(1, ...pipelineData.map((d) => d.count));

  const kpiItems = [
    { label: "Outreach", value: prospects.length, hint: "pipeline", to: "/admin/outreach" },
    { label: "Engagements", value: engagements.length, hint: "active", to: "/admin/engagements" },
    { label: "Ahead", value: schedule.ahead.length, hint: "meetings" },
    { label: "Tasks", value: openTasks, hint: "open" },
  ];

  return (
    <div className="relative min-h-0 max-w-full overflow-x-clip" data-testid="admin-home-page">
      <div className="relative p-4 sm:p-5 md:p-7">
        <header className="mb-4 sm:mb-5">
          <div className="label-eyebrow mb-1">Home</div>
          <h1
            className="font-heading text-2xl tracking-tight !font-extralight md:text-3xl"
            data-testid="home-greeting"
          >
            {timeGreeting()}
            {greetName ? `, ${greetName}` : ""}
          </h1>
          <p className="mt-1 text-sm text-[var(--t-meta)]">
            {loading
              ? "Loading…"
              : `${schedule.ahead.length} ahead · ${needsYou.length} needing attention`}
          </p>
        </header>

        {loading ? (
          <p className="py-16 text-center text-sm text-[var(--t-meta)]">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="home-top-tiles">
              {kpiItems.map((item, idx) => {
                const inner = (
                  <motion.div
                    initial={reduce ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="neo-panel h-full p-4"
                  >
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">{item.label}</div>
                    <div className="mt-1 flex items-end justify-between gap-2">
                      <div className="font-heading text-xl tracking-tight !font-light">
                        {item.value}
                      </div>
                      {item.hint ? (
                        <span className="mb-1 text-[11px] text-[var(--t-accent)]">{item.hint}</span>
                      ) : null}
                    </div>
                  </motion.div>
                );
                return item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="block transition-transform hover:-translate-y-0.5"
                    data-testid={`home-tile-${item.label.toLowerCase()}`}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div key={item.label} data-testid={`home-tile-${item.label.toLowerCase()}`}>
                    {inner}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-3">
              <Panel testId="home-horizon" className="flex h-80 max-h-80 min-h-0 flex-col">
                <PanelHead
                  eyebrow="Ahead"
                  title="On the horizon"
                  right={<span className="text-[11px] text-[var(--t-meta)]">{schedule.ahead.length}</span>}
                />
                {schedule.ahead.length === 0 ? (
                  <p className="text-sm text-[var(--t-meta)]">Clear ahead.</p>
                ) : (
                  <div className="min-h-0 flex-1 divide-y divide-black/5 overflow-y-auto">
                    {schedule.ahead.map((m) => {
                      const isNext = schedule.next?.id === m.id;
                      return (
                        <div
                          key={m.id}
                          className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                        >
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <div className="truncate text-sm font-medium">{m.title}</div>
                              {isNext ? (
                                <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-[var(--t-accent)]">
                                  Up next
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-[var(--t-meta)]">
                              {m.dayLabel} · {m.timeShort} · {m.with}
                            </div>
                          </div>
                          <JoinButton meeting={m} size="sm" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </Panel>

              <Panel testId="home-tasks" className="flex h-80 max-h-80 min-h-0 flex-col">
                <PanelHead
                  eyebrow="Work"
                  title="Tasks"
                  right={<span className="text-[11px] text-[var(--t-meta)]">{openTasks} open</span>}
                />
                {tasksLoading ? (
                  <p className="text-sm text-[var(--t-meta)]">Loading tasks…</p>
                ) : (
                  <AdminTodayWorkboard
                    tasks={homeTasks}
                    clients={engagements}
                    onToggle={onToggleHomeTask}
                    onOpenClient={onOpenHomeClient}
                    onAdd={onAddHomeTask}
                    onUpdate={onUpdateHomeTask}
                    onDelete={onDeleteHomeTask}
                  />
                )}
              </Panel>

              <Panel testId="home-outreach-glance" className="flex h-80 max-h-80 min-h-0 flex-col">
                <PanelHead
                  eyebrow="Outreach"
                  title="Pipeline glance"
                  right={
                    <Link
                      to="/admin/outreach"
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                    >
                      Open <ArrowRight size={12} />
                    </Link>
                  }
                />
                <div className="mb-2.5 flex items-baseline gap-2">
                  <div className="font-heading text-2xl tracking-tight !font-extralight text-[var(--t-accent)]">
                    {prospects.length}
                  </div>
                  <div className="text-xs text-[var(--t-meta)]">in motion</div>
                </div>
                <div className="flex min-h-0 flex-1 flex-col justify-center gap-1.5">
                  {pipelineData.map((row, idx) => {
                    const width = row.count === 0 ? 8 : Math.max(18, (row.count / maxStage) * 100);
                    return (
                      <div key={row.key} className="flex items-center gap-2">
                        <span className="w-[4.5rem] shrink-0 text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">
                          {row.label}
                        </span>
                        <div className="neo-well h-1.5 min-w-0 flex-1 overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: STAGE_BAR[row.key] }}
                            initial={reduce ? false : { width: 0 }}
                            animate={{ width: `${width}%` }}
                            transition={{ duration: 0.55, delay: idx * 0.05 }}
                          />
                        </div>
                        <span className="w-4 shrink-0 text-right text-[11px] tabular-nums">{row.count}</span>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </div>

            <section data-testid="home-engagements-folio">
              <div className="mb-3 flex items-end justify-between">
                <div className="label-eyebrow">Engagements</div>
                <div className="flex gap-3 text-[11px] text-[var(--t-meta)]">
                  {typeMix.map((t) => (
                    <span key={t.key} className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                      {t.name}
                    </span>
                  ))}
                </div>
              </div>
              {roster.length === 0 ? (
                <p className="py-12 text-center text-sm text-[var(--t-meta)]">No active engagements yet.</p>
              ) : (
                <div
                  className="flex gap-5 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] md:grid md:grid-cols-2 md:gap-5 md:overflow-visible xl:grid-cols-3"
                  data-testid="home-engagements-scroll"
                >
                  {roster.map((c, idx) => {
                    const color = offeringColor(c.service_tier);
                    return (
                      <motion.button
                        key={c.id}
                        type="button"
                        onClick={() => enterClient(c)}
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.03 }}
                        className="neo-panel relative w-[min(78vw,18rem)] shrink-0 overflow-hidden p-4 text-left md:w-auto md:min-w-0"
                        data-testid={`home-folio-${c.id}`}
                      >
                        <div className="absolute left-0 top-0 h-full w-1.5" style={{ background: color }} />
                        <div className="pl-3">
                          <div className="font-heading text-lg !font-light">{c.name}</div>
                          <div className="mt-1 text-xs text-[var(--t-meta)]">
                            {STAGE_LABEL[normalizeEngagementStage(c.engagement_stage)]} · {phaseLabel(c)}
                          </div>
                          <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]">
                            Open workspace <ArrowRight size={12} />
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
